/* eslint-disable camelcase */
/* eslint-disable react/jsx-filename-extension */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import compose from 'lodash/fp/compose';
import { useForm } from 'hooks/useForm';
import { createCreditCard, updateCreditCard, getSingleCreditCard } from 'actions';
import { removeEmptyProperties } from 'helpers/removeEmptyProperties';
// import { excludeUnsetField } from 'components/utils/excludeUnsetField';
import { format, parse } from 'date-fns';
import {
  withPersonalInfo,
  withTranslation,
  withOrgFunctionAssignment,
  withCustomRouter,
  withMsgInfo,
} from 'components/hoc';
import {
  Input,
  Select,
  InputDatePicker,
  ColumnalSelect,
  Form,
  ButtonLink,
} from 'components/common/FormItems';
import { useReferenceCheckingAccount } from 'hooks/useReferenceCheckingAccount';


const Wrapper = styled.div`
`

const StyledButtonLink = styled(ButtonLink)`
  span {
    text-transform: uppercase;
  }
`;

const StyledSelect = styled(Select)`
  padding-right: 2rem;
`;

const StyledInput = styled(Input)`
  padding-right: 1.6rem;
`;

const StyledColumnalSelect = styled(ColumnalSelect)`
  margin-right: 2rem;
`;

const StyledInputDatePicker = styled(InputDatePicker)`
  padding-right: 2rem;
`;

const personLabels = [
  {
    first_name: 'First Name',
  },
  {
    last_name: 'Last Name',
  },
];

const initialValues = {
  person_id: '',
  valid_to: '9999-12-31',
  license_org_id: '',
  account_ref: '',
  card_type: '',
  number: '',
  valid_thru: '',
  issuing_org_id: '',
  cvc: '',
};

const cardTypeOptions = [
  {
    id: 'C',
    long_description_deu: 'Kreditkarte',
    long_description_eng: 'Credit',
  },
  {
    id: 'D',
    long_description_deu: 'Debitkarte',
    long_description_eng: 'Debit',
  },
];

const validationSchema = {
  person_id: 'required',
  card_type: 'required',
  number: 'required',
  license_org_id: 'required',
  account_ref: [{ required_if: ['card_type', 'D'] }],
  valid_thru: 'required',
  issuing_org_id: 'required',
  cvc: 'required',
};

const creditCardFormSchema = {
  person_id: {
    validation: 'required',
  },
  card_type: {
    validation: 'required',
  },
  number: {
    validation: 'required',
  },
  license_org_id: {
    validation: 'required',
  },
  
  valid_thru: {
    validation: 'required',
  },
  issuing_org_id: {
    validation: 'required',
  },
  cvc: {
    validation: 'required',
  },
};

const CreditCardForm = (props) => {
  const { tr, orgFunctionAssignment, basePath, routeParams, setMsgInfo, detailMode } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formLoading, setFormLoading] = useState(false);



  const onSubmit = async (formValues, { resetForm }) => {
    setIsSubmitting(true);
    let err;
    try {
      const payload = removeEmptyProperties(formValues);
      payload.valid_thru = format(new Date(payload.valid_thru), 'MM/yy');

      // Prepare data for submission - use license_org and issuing_org as integer IDs
      const submitPayload = {
        ...payload,
        license_org_id: payload.license_org_id,
        issuing_org_id: payload.issuing_org_id,
      };

      let credit_card = {};
      if (formValues.id) {
        credit_card = await dispatch(updateCreditCard(formValues.id, submitPayload));
        setMsgInfo({
          success: true,
          msg: [`${tr('Credit card updated successfully')}`],
        });
      } else {
        credit_card = await dispatch(createCreditCard(submitPayload));
        setMsgInfo({
          success: true,
          msg: [`${tr('Credit card created successfully')}`],
        });

      }
      navigate(basePath);
      window.scrollTo(0, 0);
    } catch (e) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const {
    handleChange,
    handleBlur,
    touched,
    values,
    errors,
    setValues,
    handleSubmit,
  } = useForm({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const referenceCheckingAccount = useReferenceCheckingAccount();
  const { item } = useSelector((state) => state.creditCard);
  const { id: creditCardId } = routeParams;

  const fetchData = () => {
    if (creditCardId) {
      try {
        setFormLoading(true);
        const { ...data } = dispatch(getSingleCreditCard(creditCardId));
        setValues({ data });
      } finally {
        setFormLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData();
    return () => null;
  }, []);

  
  useEffect(() => {
    if (item) {
      const formValues = {};
      Object.keys(initialValues).forEach((key) => {
        formValues[key] = item[key];
      });
      
      setValues({ 
        ...formValues, 
        id: item.id,
        license_org_id: item.license_org_id,
        issuing_org_id: item.issuing_org_id,
      });
      return;
    }
    setValues(initialValues);
  }, [item, setValues]);


  const isError = (touchedObj, error) =>
    touchedObj && error?.length > 0 ? '  ' : '';

  // const getValidThru = (valid_thru) => {
  //   if (valid_thru && valid_thru.length <= 5) {
  //     valid_thru = valid_thru.split('/').join('/01/');
  //     return moment(new Date(valid_thru)).format('YYYY-MM-DD');
  //   }
  //   return valid_thru;
  // };

  const getValidThru = (valid_thru) => {
    if (!valid_thru) return valid_thru;
    if (valid_thru.length <= 5) {
      return format(parse(valid_thru, 'MM/yy', new Date()), 'yyyy-MM-dd');
    }
    return valid_thru;
  };



  const maskCardNumber = (cardNumber) => {
    if (!cardNumber) return '';
    // Keep only last 4 digits visible, mask the rest with asterisks
    const visibleDigits = 4;
    const maskedLength = cardNumber.length - visibleDigits;
    const maskedPart = '*'.repeat(maskedLength);
    const lastPart = cardNumber.substring(cardNumber.length - visibleDigits);
    return `${maskedPart}${lastPart}`;
  };
  
  const displayCardNumber = detailMode 
    ? maskCardNumber(values.number)
    : values.number;

    console.log("orgFunctionAssignment", orgFunctionAssignment);


  return (
    <Wrapper className="form-wrapper">
    <div className="flex">
      <StyledButtonLink
        to={basePath}
        className="option-btn"
        icon="angle double left"
        name="All Entries"
      />
    </div>
    <div className="row">
      <div
        className="col-7 ml-10 mt-30 container"
        style={{
          position: 'relative',
          background: 'var(--admincat-color-grey-1)',
        }}
      >
        <br />
          <Form
            onSubmit={handleSubmit}
            formData={values}
            isLoading={isSubmitting}
            loadingData={formLoading}
            validationSchema={creditCardFormSchema}
            title="Credit Card"
            editButtonMarginRight="3.8rem"
            editButtonGap="1rem"
          >
            <StyledSelect
              showRequiredAsterisk
              name="card_type"
              label="Debit or Credit"
              optionDefaultValue={tr('Debit or Credit')}
              value={values.card_type}
              onChange={handleChange}
              localeOptions={cardTypeOptions}
              onBlur={handleBlur}
              error={isError(touched?.card_type, errors?.card_type?.[0])}
              sort={false}
            />
            <StyledInput
              showRequiredAsterisk
              name="number"
              label="Card No"
              value={displayCardNumber}
              placeholder="Enter card number"
              onChange={handleChange}
              onBlur={handleBlur}
              error={isError(touched?.number, errors?.number?.[0])}
            />
            <StyledSelect
              showRequiredAsterisk
              name="license_org_id"
              label="Credit Card Licenser"
              value={values.license_org_id}
              optionDefaultValue={tr('Credit Card Licenser')}
              onChange={handleChange}
              options={orgFunctionAssignment.credit_card_options}
              onBlur={handleBlur}
              error={isError(touched?.license_org_id, errors?.license_org_id?.[0])}
            />
            
            <StyledInputDatePicker
              name="valid_thru"
              label="Valid to"
              showMothYear
              value={getValidThru(values.valid_thru)}
              placeholder="MM/YYYY"
              placeholderText
              showMonthYearPicker
              onChange={handleChange}
              showRequiredAsterisk
            />
            <StyledInput
              name="cvc"
              label="Card Verification Code (CVC)"
              value={values.cvc}
              placeholder="Card Verification Code (CVC)"
              onChange={handleChange}
              showRequiredAsterisk
            />
            <StyledSelect
              name="issuing_org_id"
              label="Issuing Bank Organization"
              value={values.issuing_org_id}
              optionDefaultValue={tr('Issuing organization')}
              onChange={handleChange}
              options={orgFunctionAssignment.bank_options}
              onBlur={handleBlur}
              showRequiredAsterisk
              error={isError(touched?.issuing_org_id, errors?.issuing_org_id?.[0])}
            />
            <StyledColumnalSelect
              optionDefaultValue={tr('Choose Person')}
              onChange={handleChange}
              value={values.person_id}
              name="person_id"
              label="person"
              labels={personLabels}
              options={props.personalInfo.data}
              error={isError(touched?.person_id, errors?.person_id?.[0])}
              onBlur={handleBlur}
              showRequiredAsterisk
            />

            <StyledColumnalSelect
              optionDefaultValue={tr('Choose Reference Checking Account')}
              label="reference checking account"
              // value={Number.parseInt(values.account_ref, 10)}
              value={values.account_ref ? String(values.account_ref) : ''}
              name="account_ref"
              options={referenceCheckingAccount}
              columns={['org_name', 'BIC', 'account_no']}
              labels={[
                { org_name: 'Organisation' },
                { BIC: 'BIC' },
                { account_no: 'Account Number' },
              ]}
              onChange={handleChange}
              required={values.card_type === 'D'}
            />
          </Form>
        </div>
      </div>
    </Wrapper>
  );
};

export default compose(
  withPersonalInfo,
  withTranslation,
  withOrgFunctionAssignment,
  withCustomRouter,
  withMsgInfo,
)(CreditCardForm);
